import { useEffect, useMemo, useState } from 'react'
import { Box, BoxProps, Button, Center, Container, Divider, Hide, HStack, Show } from '@chakra-ui/react'
import { Link, useRouter } from '@tanstack/react-router'

import { EventPicker } from '@components/EventPicker/EventPicker'
import { SuperbetLogo } from '@components/Icons/SuperbetLogo'
import { SuperbetLogoMini } from '@components/Icons/SuperbetLogoMini'
import { User } from '@components/User'

import { useSearchState } from '@hooks/useSearchState'

import { editorPaths, editorsList } from '@common/config'

import { useUser } from '@queries/useUser'

export function Navbar(props: BoxProps) {
    const { userData } = useUser()
    const [search, setSearchState] = useSearchState()
    const router = useRouter()
    const [activeButton, setActiveButton] = useState<string | null>(null)

    const { pathname } = router.state.location
    const isStatsAdminGroup = userData.cognitoGroups?.includes('u_stats_admin')
    const isLineupsEditorAllowed = userData.cognitoGroups?.includes('lineups_editor_admin') || isStatsAdminGroup
    const isMatchEventEditorAllowed = userData.cognitoGroups?.includes('match_events_editor_admin') || isStatsAdminGroup

    // Sync activeButton state with actual router state
    useEffect(() => {
        if (pathname.includes(editorPaths[editorsList.LINEUP_EDITOR])) {
            setActiveButton(editorsList.LINEUP_EDITOR)
        } else if (pathname.includes(editorPaths[editorsList.MATCH_EDITOR])) {
            setActiveButton(editorsList.MATCH_EDITOR)
        } else {
            setActiveButton(null)
        }
    }, [pathname])

    // Helper function to get button variant
    const getButtonVariant = (editorType: string) => {
        const isRouterActive = pathname.includes(editorPaths[editorType])
        const isImmediateActive = activeButton === editorType

        return isRouterActive || isImmediateActive ? 'mirage' : 'outline'
    }

    const lineupsEditorVariant = useMemo(() => getButtonVariant(editorsList.LINEUP_EDITOR), [pathname, activeButton])
    const matchEditorVariant = useMemo(() => getButtonVariant(editorsList.MATCH_EDITOR), [pathname, activeButton])

    // Helper function to determine current editor
    const getCurrentEditor = () => Object.keys(editorPaths).find(key => pathname.includes(editorPaths[key]))

    // Helper function to build search params
    const buildSearchParams = (targetEditor: string) => {
        const currentEditor = getCurrentEditor()
        const isSameEditor = currentEditor === targetEditor
        const baseParams = {
            ...(search?.date && { date: search?.date }),
            ...(search?.filter && { filter: search?.filter }),
        }

        if (isSameEditor) {
            // Same editor - go to list view (clear matchId)
            return baseParams
        }

        // Different editor - preserve matchId if it exists
        return {
            ...baseParams,
            ...(search?.matchId && { matchId: search?.matchId }),
        }
    }

    /** Returns path and search for TanStack Router Link (to + search, not a single URL string). */
    const getEditorLinkParams = (editorType: string) => {
        const currentEditor = getCurrentEditor()
        const isSameEditor = currentEditor === editorType
        const searchParams = buildSearchParams(editorType)

        let to = `${editorPaths[editorType]}`
        if (!search?.matchId || isSameEditor) {
            to = `${to}/todo`
        }

        return { to, search: searchParams }
    }

    // Common navigation handler
    const handleEditorChange = (editorType: string) => {
        setActiveButton(editorType)
    }

    const handleLogoClick = () => {
        setSearchState({ matchId: undefined })

        router.navigate({
            to: getHomeRoute(),
            search: prev => ({ ...prev, matchId: undefined }),
            replace: true,
        })
    }

    const getHomeRoute = () => {
        switch (true) {
            case pathname.includes(editorPaths[editorsList.LINEUP_EDITOR]) && isLineupsEditorAllowed:
                return `${editorPaths[editorsList.LINEUP_EDITOR]}/todo`

            case pathname.includes(editorPaths[editorsList.MATCH_EDITOR]) && isMatchEventEditorAllowed:
                return `${editorPaths[editorsList.MATCH_EDITOR]}/todo`

            default:
                return (
                    (isLineupsEditorAllowed && `${editorPaths[editorsList.LINEUP_EDITOR]}/todo`) ||
                    (isMatchEventEditorAllowed && `${editorPaths[editorsList.MATCH_EDITOR]}/todo`) ||
                    '/'
                )
        }
    }

    return (
        <Box as="nav" role="navigation" {...props} p={{ base: 4, xl: 8 }}>
            <Container
                flex="1"
                maxW="container.xxl"
                px={{ base: 2, sm: 4, xl: 8 }}
                py={{ base: 2, sm: 4, xl: 0 }}
                bg="white"
                borderRadius="16"
                boxShadow="lg">
                <HStack
                    gap={{ base: 4, xl: 8 }}
                    minH="20"
                    justifyContent="space-between"
                    wrap={{ base: 'wrap', xl: 'nowrap' }}>
                    <HStack gap={{ base: 2, md: 4, xl: 8 }} justifyContent="space-between">
                        <Box as="button" onClick={handleLogoClick} cursor="pointer">
                            <Show above="md">
                                <SuperbetLogo w="160px" h="36px" />
                            </Show>
                            <Hide below="34em">
                                <Hide above="md">
                                    <SuperbetLogoMini w="36px" h="36px" />
                                </Hide>
                            </Hide>
                        </Box>
                        <Hide below="34em">
                            <Center height="10">
                                <Divider orientation="vertical" />
                            </Center>
                        </Hide>
                        <Link
                            {...getEditorLinkParams(editorsList.LINEUP_EDITOR)}
                            disabled={!isLineupsEditorAllowed}
                            onClick={() => handleEditorChange(editorsList.LINEUP_EDITOR)}>
                            <Button variant={lineupsEditorVariant} isDisabled={!isLineupsEditorAllowed}>
                                Lineups Editor
                            </Button>
                        </Link>

                        <Link
                            {...getEditorLinkParams(editorsList.MATCH_EDITOR)}
                            disabled={!isMatchEventEditorAllowed}
                            onClick={() => handleEditorChange(editorsList.MATCH_EDITOR)}>
                            <Button variant={matchEditorVariant} isDisabled={!isMatchEventEditorAllowed}>
                                Match Events Editor
                            </Button>
                        </Link>
                    </HStack>
                    <Show above="xl">
                        <Center height="10">
                            <Divider orientation="vertical" />
                        </Center>
                    </Show>
                    <Box order={{ base: 2, xl: 1 }} w="100%">
                        <EventPicker />
                    </Box>
                    <HStack
                        flex={1}
                        gap={{ base: 2, md: 4, xl: 8 }}
                        order={{ base: 1, xl: 2 }}
                        justifyContent={{ base: 'end', xl: 'normal' }}>
                        <Center height="10">
                            <Divider orientation="vertical" />
                        </Center>
                        <User />
                    </HStack>
                </HStack>
            </Container>
        </Box>
    )
}
